<template>
  <div
    class="dropdown-select"
    @click="onSelectClick"
    v-click-outside="hideOptionList"
    :class="{ 'read-only': readonly, 'is-open': isOpenList }"
  >
    <div class="input-wrapper d-flex">
      <div v-if="!isOpenList && !selectedItem" class="dropdown-label">{{ placeholder }}</div>
      <input
        type="text"
        class="requiredInput"
        :value="selectedItem"
        :name="name"
        v-if="required"
        required
      />
      <div
        class="select-text flex-fill d-flex align-items-center"
        :class="{ 'select-text-placeholder': !selectedItem }"
        v-html="getItemHTML"
      ></div>
    </div>
    <input
      v-show="isOpenList"
      class="select-search"
      type="text"
      v-model="queryText"
      ref="search"
      :placeholder="placeholder"
    />
    <div v-if="shouldShowIdleHint" class="dropdown-idle-hint">
      <i class="dropdown-idle-hint-icon fal fa-info-circle" aria-hidden="true"></i>
      <div class="dropdown-idle-hint-title">{{ idleHintTitle }}</div>
      <div class="dropdown-idle-hint-text font-poppins">
        Próbáld meg a <span class="font-poppinsbold">teljes adószámot</span> beírni a gyorsabb és pontosabb szűrésért!
      </div>
    </div>
    <div class="select-option-list" v-show="isOpenList && shouldShowOptionList">
      <div v-if="loading" class="lds-ellipsis">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
      <div class="text-center py-3" v-if="filteredOptionList.length < 1 && !loading">
        {{
          options.locale && options.locale["noResults"]
            ? $cte(options.locale["noResults"])
            : ""
        }}
      </div>
      <ul v-if="filteredOptionList.length > 0 && !loading">
        <li
          v-for="(item, index) in filteredOptionList"
          v-bind:key="index"
          @click="onSelectItem(item, $event)"
          v-html="renderOption(item)"
        ></li>
      </ul>
    </div>
  </div>
</template>

<script>
import ClickOutside from "vue-click-outside";

export default {
  name: "DropDownSearch",
  props: {
    value: { type: [Object, String, Number] },
    options: { type: Object },
    externalOptionList: { type: Array },
    readonly: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    name: { type: String },
    placeholder: { type: String, default: "Kezdj el gépelni..." },
    additionalApiCallbackParams: { type: Object },
  },
  data() {
    return {
      optionList: [],
      filteredOptionList: [],
      selectedItem: null,
      queryText: null,
      isOpenList: false,
      loading: false,
      idleHintTimer: null,
      idleHintVisible: false,
    };
  },
  created: function () {
    this.updateSelectInput();
  },
  computed: {
    shouldShowOptionList: function () {
      return this.queryText && this.queryText.length >= 3;
    },

    shouldShowIdleHint: function () {
      return !!(
        this.options.idleHint &&
        this.idleHintVisible &&
        this.isOpenList &&
        this.shouldShowOptionList &&
        !this.loading &&
        this.filteredOptionList.length >= this.idleHintThreshold
      );
    },

    idleHintThreshold: function () {
      return this.options.idleHint && this.options.idleHint.threshold
        ? this.options.idleHint.threshold
        : 30;
    },

    idleHintDelay: function () {
      return this.options.idleHint && this.options.idleHint.delay
        ? this.options.idleHint.delay
        : 4000;
    },

    idleHintTitle: function () {
      return this.options.idleHint && this.options.idleHint.title
        ? this.options.idleHint.title
        : "Nem találod a céged?";
    },

    getItemHTML: function () {
      if (this.options.formatText && this.selectedItem) {
        //ha van formázó fv a kiváklasztott elemre azt használja
        return this.options.formatText(this.selectedItem);
      }

      if (this.selectedItem) {
        return this.$cte(this.selectedItem);
      }

      return this.placeholder;
    },

  },
  methods: {
    clearIdleHintTimer() {
      if (this.idleHintTimer) {
        clearTimeout(this.idleHintTimer);
        this.idleHintTimer = null;
      }
      this.idleHintVisible = false;
    },

    scheduleIdleHint() {
      this.clearIdleHintTimer();

      if (
        !this.options.idleHint ||
        !this.isOpenList ||
        !this.shouldShowOptionList ||
        this.loading ||
        this.filteredOptionList.length < this.idleHintThreshold
      ) {
        return;
      }

      this.idleHintTimer = setTimeout(() => {
        if (
          this.isOpenList &&
          this.shouldShowOptionList &&
          !this.loading &&
          this.filteredOptionList.length >= this.idleHintThreshold
        ) {
          this.idleHintVisible = true;
        }
      }, this.idleHintDelay);
    },

    updateSelectInput() {
      let self = this;
      if (self.options.apiCallback) {
        //ha van api hívás megadva akkor azt használja
        if (!self.options.searchOnServer) {
          self.loading = true;
          self.options
            .apiCallback()
            .then((response) => {
              if (self.options.mapperCallback) {
                self.optionList = self.options.mapperCallback(response.data);
              } else {
                self.optionList = response.data;
              }
              self.searchSelectedItem();
              self.optionsFiltering();
              self.loading = false;
            })
            .catch((err) => {
              console.error(err);
              self.loading = false;
            });
        } else {
          self.searchSelectedItem();
        }
      } else {
        this.optionList = this.externalOptionList; // ha nincs api ahonnan a lehetőségeket kérje, akkor külső listát használ
        this.optionsFiltering();
        self.searchSelectedItem();
      }
    },

    renderOption(data) {
      if (data && data.name && (data.fullTaxNumber || data.address)) {
        return `
          <div class="dropdown-option">
            <div class="dropdown-option-header">
              <span class="dropdown-option-name">${data.name}</span>
              <span class="dropdown-option-tax-number">${data.fullTaxNumber || ""}</span>
            </div>
            <div class="dropdown-option-address">${data.address || ""}</div>
          </div>
        `;
      }

      if (this.options.formatOption && data) {
        //ha van formázó fv a lehetőségekre akkor azt használja
        return this.options.formatOption(data);
      } else {
        return this.$cte(data);
      }
    },

    onSelectItem(data, event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.options.returnValue) {
        //ha nem obijektummal hanem egy adattagjával szeretnénk visszatérni
        this.$emit("input", data[this.options.returnValue]);
      } else {
        this.$emit("input", data);
      }

      this.selectedItem = data;
      this.hideOptionList();
      this.$emit("select", data);
    },

    searchSelectedItem() {
      let self = this;
      if (self.value !== null && self.value !== undefined) {
        if (self.options.returnValue) {
          self.selectedItem = self.optionList.find((value) => {
            return value[self.options.returnValue] == self.value;
          });

          if (!self.selectedItem || self.selectedItem === "") {
            self.selectedItem = {};
            if (typeof self.value === "object") {
              self.selectedItem = self.value;
            } else {
              self.selectedItem[self.options.filteredAttr] = self.value;
            }
          }
        } else {
          self.selectedItem = self.value;

          if (!self.selectedItem[self.options.filteredAttr]) {
            self.selectedItem = {};
            if (typeof self.value === "object") {
              self.selectedItem = self.value;
            } else {
              self.selectedItem[self.options.filteredAttr] = self.value;
            }
          }
        }
      }
    },

    optionsFiltering() {
      let self = this;

      if (!self.options.minimumInputLength) {
        if (this.options.searchOnServer) {
          self.searchOnServer();
        } else {
          self.searchOnLocale();
        }
      } else {
        if (
          self.queryText &&
          self.queryText.length >= self.options.minimumInputLength
        ) {
          if (this.options.searchOnServer) {
            self.searchOnServer();
          } else {
            self.searchOnLocale();
          }
        } else {
          self.filteredOptionList = [];
        }
      }
    },

    searchOnServer() {
      let self = this;
      self.loading = true;
      this.options
        .apiCallback({
          params: {
            ...self.additionalApiCallbackParams,
            term: self.queryText,
            type: "query",
          },
        })
        .then((response) => {
          if (self.options.mapperCallback) {
            self.filteredOptionList = self.options.mapperCallback(
              response.data
            );
          } else {
            self.filteredOptionList = response.data;
          }
          self.loading = false;
          self.scheduleIdleHint();
        })
        .catch((err) => {
          console.error(err);
          self.loading = false;
        });
    },

    searchOnLocale() {
      let self = this;
      self.filteredOptionList = self.optionList.filter((value) => {
        if (self.options.filteredAttr && self.queryText) {
          return value[self.options.filteredAttr]
            .toLowerCase()
            .includes(self.queryText.toLowerCase());
        }
        return true;
      });
      self.scheduleIdleHint();
    },

    onSelectClick() {
      if (!this.readonly) {
        let self = this;
        self.isOpenList = true;
        setTimeout(function () {
          self.$refs.search.focus();
        }, 10);
      }
    },

    hideOptionList() {
      this.isOpenList = false;
      this.clearIdleHintTimer();
      this.resetSelect();
    },

    resetSelect() {
      this.queryText = null;
    },
  },

  watch: {
    queryText: function () {
      this.clearIdleHintTimer();
      this.optionsFiltering();
    },

    value: function (val) {
      if (!val || val === "") {
        this.selectedItem = undefined;
      }

      this.updateSelectInput();
    },
  },

  beforeDestroy() {
    this.clearIdleHintTimer();
  },

  directives: {
    ClickOutside,
  },
};
</script>

<style scoped>
.dropdown-select {
  position: relative;
  width: 100%;
  font-family: inherit;
}

.dropdown-select * {
  cursor: pointer;
}

.dropdown-select.read-only {
  opacity: 0.7;
}

.dropdown-label {
  font-size: 12px;
  line-height: 18px;
  color: #323333;
  margin: 0px;
}

.input-wrapper {
  align-items: center;
  background: white !important;
  border: 1px solid #aebfc7;
  border-radius: 24px;
  box-shadow: 0 10px 24px rgba(37, 39, 52, 0.09);
  height: 40px;
  max-width: 100%;
  padding: 0 18px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.dropdown-select.is-open .input-wrapper {
  border-color: #aebfc7;
  box-shadow: 0 10px 24px rgba(37, 39, 52, 0.09);
}

.dropdown-select.is-open .select-text {
  display: none !important;
}

.requiredInput {
  position: absolute;
  z-index: -1000;
  width: 1px;
  height: 1px;
  border: none;
}

.select-text {
  color: #323333;
  font-family: Poppins, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.select-clear-wrapper button {
  background-color: transparent;
  color: #888888;
  border: none;
}

.dropdown-idle-hint {
  background: #ffffff;
  border: 1px solid #f4c400;
  border-radius: 24px;
  bottom: calc(100% + 10px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  color: #333333;
  font-family: Poppins, sans-serif;
  max-width: min(50%, 250px);
  padding: 18px 24px;
  position: absolute;
  right: 0;
  width: 50%;
  z-index: 1200;
}

.dropdown-idle-hint-icon {
  align-items: center;
  color: #000000;
  display: flex;
  font-size: 10px;
  font-weight: 600;
  height: 24px;
  justify-content: center;
  line-height: 1;
  position: absolute;
  right: 18px;
  top: 18px;
  width: 24px;
}

.dropdown-idle-hint-title {
  color: #333333;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.2;
  margin: 0 42px 12px 0;
}

.dropdown-idle-hint-text {
  color: #444444;
  font-size: 14px;
  font-weight: 400 !important;
  line-height: 1.2;
}

.dropdown-idle-hint-text span {
  font-weight: 400 !important;
}

.dropdown-idle-hint-text strong {
  font-weight: 700 !important;
}

.select-option-list {
  background: #ffffff;
  border: 1px solid #aebfc7;
  border-radius: 20px !important;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  left: 0;
  margin-top: 8px;
  max-height: 450px;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 100%;
  width: 100%;
  z-index: 1000;
}

.select-search {
  color: #323333;
  cursor: text;
  font-size: 14px;
  line-height: 20px;
  outline: none;
  padding: 14px 18px !important;
  width: 100%;
  -webkit-appearance: none;
}

#app input.select-search {
  background-color: #ffffff !important;
  border: 1px solid #aebfc7 !important;
  border-radius: 24px !important;
  box-shadow: 0 10px 24px rgba(37, 39, 52, 0.09) !important;
  height: 40px !important;
  left: 0 !important;
  margin-top: 0 !important;
  padding: 0 18px !important;
  position: absolute !important;
  top: 0px !important;
  width: 100% !important;
  z-index: 1001 !important;
}

.dropdown-select.is-open #app input.select-search,
#app .dropdown-select.is-open input.select-search {
  color: transparent !important;
}

.dropdown-select.is-open #app input.select-search:focus,
#app .dropdown-select.is-open input.select-search:focus {
  color: #333333 !important;
}

.select-search::placeholder {
  color: #323333;
}

.select-text-placeholder {
  color: #323333;
}

.select-option-list ul {
  list-style: none;
  margin: 0;
  max-height: 450px;
  overflow-y: auto;
  padding: 0;
}

.select-option-list ul li {
  border-bottom: 1px solid #f5f5f5;
  color: #323333;
  font-size: 14px;
  line-height: 20px;
  padding: 12px 18px;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  -webkit-user-select: none;
  cursor: pointer;
}

.select-option-list ul li:last-child {
  border-bottom: none;
}

.select-option-list ul li:hover {
  background-color: #f9f9f9;
  color: #323333;
}

.select-option-list ul li::v-deep .dropdown-option {
  width: 100%;
}

.select-option-list ul li::v-deep .dropdown-option-header {
  align-items: flex-start;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  margin-bottom: 4px;
  min-width: 0;
}

.select-option-list ul li::v-deep .dropdown-option-name {
  color: #323333;
  flex: 1 1 auto;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.select-option-list ul li::v-deep .dropdown-option-tax-number {
  color: #9e9e9e;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-align: right;
  white-space: nowrap;
}

.select-option-list ul li::v-deep .dropdown-option-address {
  color: #9e9e9e;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/*Loader*/

.lds-ellipsis {
  display: block;
  position: relative;
  width: 64px;
  height: 35px;
  margin: auto;
}

.lds-ellipsis div {
  position: absolute;
  top: 12px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: #5bb65d;
  animation-timing-function: cubic-bezier(0, 1, 1, 0);
}

.lds-ellipsis div:nth-child(1) {
  left: 6px;
  animation: lds-ellipsis1 0.6s infinite;
}

.lds-ellipsis div:nth-child(2) {
  left: 6px;
  animation: lds-ellipsis2 0.6s infinite;
}

.lds-ellipsis div:nth-child(3) {
  left: 26px;
  animation: lds-ellipsis2 0.6s infinite;
}

.lds-ellipsis div:nth-child(4) {
  left: 45px;
  animation: lds-ellipsis3 0.6s infinite;
}

@keyframes lds-ellipsis1 {
  0% {
    transform: scale(0);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes lds-ellipsis3 {
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(0);
  }
}

@keyframes lds-ellipsis2 {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(19px, 0);
  }
}
</style>
